import {useViewQuery} from '@tnc/shared';
import clsx from 'clsx';
import {useState} from 'react';
import {Link, useNavigate} from 'react-router';

import {dataTableActionT, dataTableColumnT} from '@/lib/types/dataTable.types';
import {SelectedColumnsT} from '@/lib/types/query.types';
import {statsRowT} from '@/lib/types/stats.types';

import {usdFormatter} from '@/lib/helpers';
import {createDataTableConfig} from '@/lib/helpers/config';

import {StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';
import DefaultTableAvatarImage from '@/components/defaultTableAvatarImage';

/*******************************************************************
 * Admin Positions Page
 *******************************************************************/
function AdminPositions() {
  const navigate = useNavigate();
  const [promptNavModal, setPromptNavModal] = useState(false);
  // TanStack queries
  const {data: stats} = useViewQuery({
    view: 'admin_position_stats',
  });

  // Data Table -----------------------------------------------------
  const {table, title, columns, searchColumn} = createDataTableConfig({
    table: 'Position',
    title: 'Positions Summary',
    searchColumn: 'Speculator.speculator_username',
    sortOptions: [
      {
        column: 'date_created',
        label: 'Date Created',
        direction: 'desc',
      },
    ],
    columns: [
      'position_id',
      'position_factored_price_total',
      'position_status',
      'postion_margin_total',
      'date_created',
      'created_by',
      'fk_market_id',
      'fk_brand_id',
      'fk_product_id',
      'position_order_type',
      'position_item_unit_market_cost',
      'position_item_unit_factored_cost',
      'position_order_sub_type',
      ['Speculator', ['speculator_username', 'speculator_id']],
      ['Brand', ['brand_name', 'brand_id', 'brand_logo_url']],
      ['Product', ['product_name', 'product_id', 'product_image_url']],
      ['Market', ['market_name', 'market_id']],
    ],
  });

  // Helper types to make the code cleaner and DRY
  type DataTableT = typeof table;
  type DataTableColumnsT = typeof columns;
  type DataTableRowT = SelectedColumnsT<DataTableT, DataTableColumnsT>;

  // Table Columns
  const columnsDisplay: dataTableColumnT<DataTableT, DataTableRowT>[] = [
    {
      header: 'Position ID',
      field: 'position_id',
      render: row => (
        <Link className="text-primary hover:text-primary dark:text-primary" to={`/positions/${row.position_id}`}>
          {row.position_id}
        </Link>
      ),
    },
    {
      header: 'Speculator',
      field: 'fk_speculator_id',
      render: row => (
        <Link
          className="text-primary hover:text-primary dark:text-primary"
          to={`/admin/speculators/${row.Speculator.speculator_id}`}
        >
          {/*  // HAS ALL POSSIBLE PROPERTIES */}
          {row.Speculator.speculator_username}
        </Link>
      ),
    },
    {
      header: 'Market',
      field: 'fk_market_id',
      render: row => (
        <Link className="text-primary hover:text-primary dark:text-primary" to={`/markets/${row.Market.market_id}`}>
          {row.Market.market_name}
        </Link>
      ),
    },
    {
      header: 'Brand',
      field: 'fk_brand_id',
      render: row => (
        <Link
          className="flex min-w-[125px] items-center gap-2 text-primary hover:text-primary dark:text-primary"
          to={`/brands/${row.Brand.brand_id}`}
        >
          <DefaultTableAvatarImage
            src={row.Brand.brand_logo_url || '/images/logo/logo.png'}
            alt={row.Brand.brand_name}
          />
          {row.Brand.brand_name}
        </Link>
      ),
    },
    {
      header: 'Product',
      field: 'fk_product_id',
      render: row => (
        <Link
          className="flex min-w-[125px] items-center gap-2 text-primary hover:text-primary dark:text-primary"
          to={`/products/${row.Product.product_id}`}
        >
          <DefaultTableAvatarImage src={row.Product.product_image_url || '/images/logo/logo.png'} alt="Product Image" />
          {row.Product.product_name}
        </Link>
      ),
    },
    {
      header: 'Factored Cost',
      field: 'position_factored_price_total',
    },
    {
      header: 'Status',
      field: 'position_status',
      render: row => {
        return (
          <span
            className={clsx('rounded-md p-1 text-xs capitalize text-black/80', {
              'bg-success': row.position_status === 'active',
              'bg-danger': row.position_status === 'closed',
              'bg-info': row.position_status === 'pending',
            })}
          >
            {row.position_status}
          </span>
        );
      },
    },
    {
      header: 'Margin Total',
      field: 'postion_margin_total',
      render: row => <span>{usdFormatter.format(row.postion_margin_total)}</span>,
    },
    {
      header: 'Date Created',
      field: 'date_created',
      render: row => (
        <span>
          {new Date(row.date_created).toLocaleDateString('en-US', {month: 'numeric', day: 'numeric', year: '2-digit'})}
        </span>
      ),
    },
  ];

  // Table actions
  const actions: dataTableActionT<DataTableT, DataTableRowT>[] = [];

  // Stats ---------------------------------------------------------
  const statsRow: statsRowT = [
    {
      title: 'Total Positions',
      value: stats?.data?.[0]?.total ?? 0,
      subtitle: 'This Month',
      icon: 'ri-stack-line',
      color: 'primary',
      change: 1.03,
      cardClass: 'Stats-main-card primary',
    },
    {
      title: 'Total Retail Price',
      value: usdFormatter.format(stats?.data?.[0]?.totalRetailPrice ?? 0),
      subtitle: 'This Month',
      icon: 'ri-money-dollar-circle-line',
      color: 'warning',
      change: -1.28,
      cardClass: 'Stats-main-card warning',
    },
    {
      title: 'Potential Profit',
      value: usdFormatter.format(stats?.data?.[0]?.potentialProfit ?? 0),
      subtitle: 'This Month',
      icon: 'ri-money-dollar-box-line',
      color: 'secondary',
      // change is the total potential profit / total retail price
      change: Number((stats?.data?.[0]?.potentialProfitChange ?? 0) * 100),
      cardClass: 'Stats-main-card secondary',
    },
  ];

  return (
    <>
      <Page currentPage="Positions">
        <StatsRowComponent stats={statsRow} />
        <DataTable
          // Data -----------------------------------
          table={table}
          columns={columns}
          // Options --------------------------------
          searchColumn={searchColumn}
          // Sort options a loaded from sortOptions.ts, unless you want to use custom options
          // sortOptions={CUSTOM_OPTIONS} // sortOptions<T>[]
          // Data display ---------------------------
          title={title} // Optional
          columnsDisplay={columnsDisplay}
          actionsDisplay={actions}
          // Display --------------------------------
          className="" // Applies to outer-most div
          // Display Optionals ----------------------
          // raw
          // hideSort
          // hideRefresh
          // hideSearch
          // hideAdd
          // hideTitle
          // hideShowing
          // hideLimit
          // hideLimitIcon
          // hidePagination
          // Events ---------------------------------
          // onSetLimit={(limit: limitT) => {
          //   console.log('limit', limit);
          // }}
          // onSearch={(searchTerm: string) => {
          //   console.log('search', searchTerm);
          // }}
          // onRefresh={() => {
          //   console.log('refresh');
          // }}
          onAdd={() => setPromptNavModal(true)}
          // onGoToPage={page => {
          //   console.log('page', page);
          // }}
          // onPrev={() => {
          //   console.log('prev');
          // }}
          // onNext={() => {
          //   console.log('next');
          // }}
        />
      </Page>

      {/* Modal to select wholesale or market positions to navigate to */}
      <div
        className={`hs-overlay ${promptNavModal ? 'flex' : 'hidden'} ti-modal fixed inset-0 items-center justify-center bg-black bg-opacity-30`}
      >
        <div className="ti-modal-box modal-dialog-centered mt-0 max-h-[80vh] w-full overflow-hidden !opacity-100 ease-out hs-overlay-open:mt-7 sm:w-1/2 md:w-1/3 lg:w-1/4">
          <div className="ti-modal-content flex h-full flex-col rounded-lg bg-white shadow-lg">
            <div className="ti-modal-header flex-shrink-0 border-b p-4">
              <h6 className="modal-title !text-default text-[1rem] font-semibold dark:text-defaulttextcolor/70">
                Select Position Type
              </h6>
            </div>
            <div className="ti-modal-body px-6 py-4">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12 xl:col-span-6">
                  <button
                    className="ti-btn w-full bg-primary"
                    onClick={() => navigate('/admin/positions/new/wholesale')}
                  >
                    Wholesale Positions
                  </button>
                </div>
                <div className="col-span-12 xl:col-span-6">
                  <button className="ti-btn w-full bg-primary" onClick={() => navigate('/admin/positions/new/market')}>
                    Market Positions
                  </button>
                </div>
              </div>
            </div>
            <div className="ti-modal-footer flex-shrink-0 border-t p-4">
              <button className="ti-btn w-full bg-danger" onClick={() => setPromptNavModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminPositions;
